import { useCallback, useEffect, useRef } from 'react';
import { takeUntil } from 'rxjs';
import { createDataFeedInput } from '../utils/chart.util';
import { useFileUpload } from '../utils/file.util';
import { ChartState, useChartState } from '@client/state/chart-state.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';

export const useHandleLastUpdatedChart = () => {
  const { getLastUpdatedChart$ } = useChartState();
  const { uploadFile } = useFileUpload();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const timeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const handleChartUpdate = useCallback(
    (chart: ChartState) => {
      const { id, formState } = chart;
      const { marketConfig, timeframes = [] } = formState;
      const { tradingMode, useExchangeData } = marketConfig || {};

      const files = timeframes
        .map(({ file }) => file)
        .filter((file): file is File => file !== undefined);

      if (
        tradingMode === 'BACKTESTING' &&
        (files.length > 0 || useExchangeData)
      ) {
        const timeoutId = setTimeout(() => {
          uploadFile(createDataFeedInput(formState, id), files);
          timeoutIdsRef.current.delete(timeoutId);
        }, 500);
        timeoutIdsRef.current.add(timeoutId);
      }
    },
    [uploadFile]
  );

  useEffect(() => {
    const subscription = getLastUpdatedChart$()
      .pipe(takeUntil(workspaceDestroy$))
      .subscribe(handleChartUpdate);

    return () => {
      subscription.unsubscribe();
      timeoutIdsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutIdsRef.current.clear();
    };
  }, [getLastUpdatedChart$, handleChartUpdate, workspaceDestroy$]);
};
